// src/Auth/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { loginService } from "../services/AuthServices";
import toast from 'react-hot-toast';

// Constants for User Roles and Agent Types
const USER_ROLES = {
  ORGANIZATION: 'organization',
  AGENT: 'agent',
  ADMIN: 'admin'
};

const AGENT_TYPES = {
  SALES: 'sales',
  MARKETING: 'marketing',
  SUPPORT: 'support',
  GENERAL: 'general'
};

// Enhanced JWT decoding with better error handling
const decodeJWT = (token) => {
  try {
    if (!token) {
      return null;
    }

    // Remove 'Bearer ' prefix if present
    const actualToken = token.startsWith('Bearer ') ? token.slice(7) : token;

    // JWT tokens have 3 parts: header.payload.signature
    const parts = actualToken.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];

    // Add padding if necessary and decode
    const paddedPayload = payload.padEnd(payload.length + (4 - payload.length % 4) % 4, '=');

    // Base64 decode and parse JSON
    const decodedPayload = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));
    return decodedPayload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Enhanced user creation with better type safety
const createUserFromToken = (decodedToken, email, apiResponse) => {
  // Validate and extract role from decoded token
  const tokenRole = decodedToken.role;
  let role = USER_ROLES.ADMIN; // default role

  // Validate role against allowed roles - use actual role from API
  if (tokenRole === USER_ROLES.AGENT ||
    tokenRole === USER_ROLES.ADMIN ||
    tokenRole === USER_ROLES.ORGANIZATION) {
    role = tokenRole;
  }

  // Create base user object with fallbacks
  const user = {
    id: decodedToken.account_id || decodedToken.user_id || decodedToken.sub || "1",
    email: email,
    name: decodedToken.name || decodedToken.username || email.split('@')[0],
    role: role,
    organizationId: decodedToken.tag_id || decodedToken.organization_id || decodedToken.org_id || "org_1",
    tag_id: decodedToken.tag_id || "11",
    permissions: decodedToken.permissions || decodedToken.scope || ["*"],
    avatar: decodedToken.avatar || apiResponse?.avatar || `https://ui-avatars.com/api/?name=${email.split('@')[0]}&background=random`,
    // FIXED: Prioritize API response accessPath, then fallback to decoded token
    accessPath: apiResponse?.accessPath || decodedToken.accessPath || [],
  };

  // Add agentType if the role is agent
  if (role === USER_ROLES.AGENT) {
    user.agentType = (decodedToken.agent_type || decodedToken.agentType || AGENT_TYPES.SALES);
  }

  return user;
};

// Validate user object
const isValidUser = (user) => {
  if (!user || typeof user !== 'object') return false;
  if (!user.id || !user.email || !user.name || !user.role || !user.permissions) return false;
  if (!Object.values(USER_ROLES).includes(user.role)) return false;
  if (user.agentType && !Object.values(AGENT_TYPES).includes(user.agentType)) return false;
  return true;
};

// Check if token is expired - SIMPLIFIED VERSION
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decodedToken = decodeJWT(token);
    if (!decodedToken) return true;
    
    // Only check expiration if exp field exists
    if (decodedToken.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = decodedToken.exp <= currentTime;
      return isExpired;
    }
    
    // If no exp field, assume token is valid (don't auto-expire)
    return false;
  } catch (error) {
    return true;
  }
};

// Create Auth Context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  
  // Use ref to track active toast - more reliable than state
  const activeToastRef = useRef(null);

  // Clear all authentication storage
  const clearAuthStorage = useCallback(() => {
    if (typeof window !== 'undefined') {
      // window.localStorage.removeItem("authToken");
      window.localStorage.removeItem("authUser");
      window.localStorage.removeItem("authRole");
      window.localStorage.removeItem("accountId");
      // window.localStorage.removeItem("token");
    }
  }, []);

  // Clear session (alias for signout)
  const clearSession = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    clearAuthStorage();
  }, [clearAuthStorage]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      if (typeof window !== 'undefined') {
        // Check both possible token keys for compatibility
        const storedToken = window.localStorage.getItem("authToken") || window.localStorage.getItem("token");
        const storedUser = window.localStorage.getItem("authUser");

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);

            // Validate stored user data
            if (!isValidUser(parsedUser)) {
              throw new Error("Invalid user data in storage");
            }

            // Check token expiration ONLY if exp field exists
            // If no exp field, assume token is valid
            if (isTokenExpired(storedToken)) {
              clearSession();
              setIsLoading(false);
              return;
            }

            setIsAuthenticated(true);
            setUser(parsedUser);
            setToken(storedToken);
          } catch (error) {
            clearSession();
          }
        }
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [clearSession]);

  // Enhanced signin with proper toast timing
  const signin = useCallback(async (enteredEmail, enteredPassword) => {
    // Dismiss ALL active toasts at the start
    toast.dismiss();
    if (activeToastRef.current) {
      toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
    }

    try {
      setIsLoading(true);

      const payload = {
        email: enteredEmail,
        password: enteredPassword,
      };

      // API call to login endpoint
      const response = await loginService(payload);

      // Handle your specific API response structure
      if (response?.status === true && response?.token) {
        const apiToken = response.token;

        // Decode the JWT token to get user information
        const decodedToken = decodeJWT(apiToken);

        if (!decodedToken) {
          const errorToast = toast.error("Invalid token received", {
            position: 'top-right',
            duration: 4000,
          });
          activeToastRef.current = errorToast;
          setIsLoading(false);
          return {
            success: false,
            message: "Invalid token received"
          };
        }

        // Create user object from decoded token and API response
        const user = createUserFromToken(decodedToken, enteredEmail, response);

        // Validate user object
        if (!isValidUser(user)) {
          const errorToast = toast.error("Invalid user data received", {
            position: 'top-right',
            duration: 4000,
          });
          activeToastRef.current = errorToast;
          setIsLoading(false);
          return {
            success: false,
            message: "Invalid user data received"
          };
        }

        console.log(apiToken, user, "cckkll")

        // Store in localStorage - use consistent keys
        if (typeof window !== 'undefined') {
          window.localStorage.setItem("authToken", apiToken);
          window.localStorage.setItem("token", apiToken);
          window.localStorage.setItem("authUser", JSON.stringify(user));
          window.localStorage.setItem("authRole", user.role);
          window.localStorage.setItem("tagId", user.tag_id);
          window.localStorage.setItem("accountId", decodedToken.account_id || user.id);
        }

        // Update state FIRST
        setIsAuthenticated(true);
        setUser(user);
        setToken(apiToken);

        // Show success toast
        const successToast = toast.success("Login successful! Redirecting...", {
          duration: 2000,
          position: 'top-right',
        });
        activeToastRef.current = successToast;

        // Determine where to navigate based on accessPath
        let redirectPath = '/dashboard'; // Default fallback

        // Check if accessPath exists and has items
        if (user.accessPath && Array.isArray(user.accessPath) && user.accessPath.length > 0) {
          // Use the first path from accessPath
          redirectPath = user.accessPath[0];
          
          // If the path is empty or just "/", use dashboard
          if (redirectPath === '/' || redirectPath === '' || redirectPath === null || redirectPath === undefined) {
            // Try to find a valid path in the array
            const validPath = user.accessPath.find(path => 
              path && path !== '/' && path !== '' && path !== null && path !== undefined
            );
            
            if (validPath) {
              redirectPath = validPath;
            } else {
              redirectPath = '/dashboard';
            }
          }
        } else {
          // AccessPath is empty, null, or undefined - go to dashboard
          console.log('AccessPath is empty, redirecting to dashboard');
          redirectPath = '/dashboard';
        }

        console.log('User accessPath:', user.accessPath);
        console.log('Redirecting to:', redirectPath);

        // Use a flag to prevent navigation if component unmounts
        let isMounted = true;

        // Wait for toast to complete (2 seconds) then navigate
        setTimeout(() => {
          if (isMounted) {
            setIsLoading(false);
            // Use replace to prevent back button issues
            navigate(redirectPath, { replace: true });
          }
        }, 2000);

        return {
          success: true,
          user,
          token: apiToken,
          message: "Login successful",
          redirectPath
        };
      } else {
        // Show error toast for invalid credentials
        const errorMessage = response?.message || "Invalid email or password";
        const errorToast = toast.error(errorMessage, {
          position: 'top-right',
          duration: 4000,
        });
        activeToastRef.current = errorToast;
        setIsLoading(false);
        
        return {
          success: false,
          message: errorMessage
        };
      }

    } catch (error) {
      // Dismiss any loading toast
      if (activeToastRef.current) {
        toast.dismiss(activeToastRef.current);
      }
      
      // Show error toast
      const errorMessage = error?.response?.data?.message || "Login failed. Please try again.";
      const errorToast = toast.error(errorMessage, {
        position: 'top-right',
        duration: 5000,
      });
      activeToastRef.current = errorToast;
      setIsLoading(false);
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }, [navigate]);

  // Enhanced signout with toast
  const signout = useCallback((showToast = true) => {
    // Dismiss all active toasts
    toast.dismiss();
    if (activeToastRef.current) {
      toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
    }

    // Show logout toast if requested
    if (showToast) {
      const logoutToast = toast.success("Logged out successfully!", {
        position: 'top-right',
        duration: 2000,
      });
      activeToastRef.current = logoutToast;
    }

    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);

    // Clear localStorage
    clearAuthStorage();

    // Wait for toast to complete, then navigate
    setTimeout(() => {
      navigate('/signin', { replace: true });
    }, 2000);
  }, [navigate, clearAuthStorage]);

  // Simple refresh token function
  const refreshToken = useCallback(async () => {
    return false;
  }, []);

  const hasRole = useCallback((role) => {
    if (!user || !role) return false;
    return user.role === role;
  }, [user]);

  const hasPermission = useCallback((permission) => {
    if (!user || !permission) return false;
    return user.permissions.includes("*") || user.permissions.includes(permission);
  }, [user]);

  const updateUser = useCallback((updates) => {
    if (!user || !updates) return;

    const updatedUser = {
      ...user,
      ...updates
    };

    if (isValidUser(updatedUser)) {
      setUser(updatedUser);

      // Update localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem("authUser", JSON.stringify(updatedUser));
      }
      
      // Dismiss any active toasts first
      if (activeToastRef.current) {
        toast.dismiss(activeToastRef.current);
      }
      
      // Show success toast that auto-closes
      const updateToast = toast.success("Profile updated successfully!", {
        position: 'top-right',
        duration: 2000,
      });
      activeToastRef.current = updateToast;
      
      return true;
    }

    return false;
  }, [user]);

  // Check if user has access to a specific path
  const hasAccessToPath = useCallback((path) => {
    if (!user) return false;
    
    // If user is admin and accessPath is empty, allow all paths
    if (user.role === USER_ROLES.ADMIN && (!user.accessPath || user.accessPath.length === 0)) {
      return true;
    }
    
    // If accessPath is empty for non-admin, deny access
    if (!user.accessPath || user.accessPath.length === 0) {
      return false;
    }

    // Check exact match
    if (user.accessPath.includes(path)) {
      return true;
    }

    // Check for subpaths (e.g., /agent-pov/settings should be allowed if /agent-pov is in accessPath)
    return user.accessPath.some(accessPath => {
      if (path.startsWith(accessPath + '/')) {
        return true;
      }
      return false;
    });
  }, [user]);

  // Get first available path for user
  const getFirstAvailablePath = useCallback(() => {
    if (!user) return '/dashboard';
    
    // Case 1: User has accessPath with items
    if (user.accessPath && Array.isArray(user.accessPath) && user.accessPath.length > 0) {
      const firstPath = user.accessPath[0];
      
      // Handle various invalid path cases
      if (!firstPath || firstPath === '/' || firstPath === '' || firstPath === null || firstPath === undefined) {
        // If first path is invalid, try to find a valid one
        const validPath = user.accessPath.find(path => 
          path && path !== '/' && path !== '' && path !== null && path !== undefined
        );
        
        if (validPath) {
          return validPath;
        }
        
        // If no valid paths found, go to dashboard
        return '/dashboard';
      }
      
      return firstPath;
    }
    
    // Case 2: User is admin with empty accessPath - go to dashboard
    if (user.role === USER_ROLES.ADMIN) {
      console.log('Admin with empty accessPath - redirecting to dashboard');
      return '/dashboard';
    }
    
    // Case 3: No accessPath at all - default to dashboard
    console.log('No accessPath found - redirecting to dashboard');
    return '/dashboard';
  }, [user]);

  // Memoize context value
  const value = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    token,
    signin,
    signout,
    hasRole,
    hasPermission,
    hasAccessToPath,
    getFirstAvailablePath,
    updateUser,
    refreshToken,
    clearSession,
    USER_ROLES,
    AGENT_TYPES
  }), [
    isAuthenticated,
    isLoading,
    user,
    token,
    signin,
    signout,
    hasRole,
    hasPermission,
    hasAccessToPath,
    getFirstAvailablePath,
    updateUser,
    refreshToken,
    clearSession
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("AuthContext must be used within AuthProvider");
  return ctx;
};

// Export constants for use in other files
export { USER_ROLES, AGENT_TYPES };